import { useCallback } from 'react';
import { toast } from 'sonner';

import { useAuthInfo } from '@/allauth_auth/hooks';
import {
  PropertyTypeConflictError,
  PropertyTypePermissionError,
  PropertyTypeValidationError,
} from '@/lib/crm/core/errors';
import type { AssociationType } from '@/lib/crm/core/SchemaClient';
import type { EntityTypeName } from '@/lib/crm/core/types';
import type { PropertySchemaMap } from '@/lib/crm/hooks/usePropertySchema';
import { applyObjectMerged } from '@/lib/crm/store/crmObjectStore';
import { submitEventsOptimistic } from '@/lib/crm/store/submitEventsOptimistic';
import { ApiError } from '@/lib/errors';
import { logger } from '@/lib/logger';
import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

import { useMergeWizard } from './MergeWizardContext';
import type {
  MultiPropertyResolution,
  SinglePropertyResolution,
} from './types';
import { computeMergePropertyResolution } from './useMergePropertyResolution';

export interface UseMergeSubmitOptions {
  orgId: number;
  entityTypeName: EntityTypeName;
  entityIds: [string, string];
  onSuccess: (canonicalEntityId: string) => void;
  /** Optional accessor for the source object's associations (for batch 2 lookup). Provided by MergeWizard. */
  associationsByEntityId: Record<
    string,
    Array<{ association_type_name: string; target_entity_id: string }>
  >;
  /**
   * Full canonical/source objects + schema, used to transfer auto-resolved
   * one-sided source values (fields the source has and the canonical lacks)
   * that the user never interacts with in Step 2. Without these, such values
   * are silently lost on merge. Provided by MergeWizard; optional so the hook
   * degrades gracefully (and stays testable) when they are unavailable.
   */
  canonicalObject?: CrmObject;
  sourceObject?: CrmObject;
  schema?: PropertySchemaMap;
  /**
   * Association types (with cardinality), used to enforce `*_to_one` picks
   * (e.g. which single company the merged record keeps). Provided by
   * MergeWizard; optional so the hook degrades gracefully in tests.
   */
  associationTypes?: AssociationType[];
}

export interface UseMergeSubmitResult {
  submit: () => Promise<void>;
}

export function useMergeSubmit(
  opts: UseMergeSubmitOptions
): UseMergeSubmitResult {
  const {
    orgId,
    entityTypeName,
    onSuccess,
    associationsByEntityId,
    canonicalObject,
    sourceObject,
    schema,
    associationTypes,
  } = opts;

  const {
    canonicalId,
    sourceId,
    singlePropertyResolutions,
    multiPropertyResolutions,
    dismissedAssociationIds,
    associationChoices,
  } = useMergeWizard();

  const { user } = useAuthInfo();

  const submit = useCallback(async () => {
    if (!canonicalId || !sourceId) {
      return;
    }

    const resolutions: Record<string, unknown> = {};

    for (const [propertyName, resolution] of Object.entries(
      singlePropertyResolutions
    ) as [string, SinglePropertyResolution][]) {
      if (resolution.winner === 'source') {
        resolutions[propertyName] = getPropertyValue<unknown>(
          resolution.sourceValue
        );
      }
      // canonical wins → no entry needed
    }

    // Auto-resolved one-sided values: a field the source has and the canonical
    // lacks is auto-selected to the source but never written to context (the
    // user doesn't interact with it). Fold those source-winners in so they
    // actually transfer — otherwise the merge silently drops them even though
    // the Step 2 preview shows them carrying over.
    if (canonicalObject && sourceObject && schema) {
      const { autoResolved } = computeMergePropertyResolution(
        canonicalObject,
        sourceObject,
        schema
      );
      for (const resolution of autoResolved) {
        if (
          resolution.winner === 'source' &&
          !(resolution.propertyName in resolutions)
        ) {
          resolutions[resolution.propertyName] = getPropertyValue<unknown>(
            resolution.sourceValue
          );
        }
      }
    }

    for (const [propertyName, multi] of Object.entries(
      multiPropertyResolutions
    ) as [string, MultiPropertyResolution][]) {
      const kept = [...multi.canonicalItems, ...multi.sourceItems]
        .filter(it => it.keep)
        .map(it => getPropertyValue<unknown>(it.value));
      const originalCanonical = multi.canonicalItems.map(it =>
        getPropertyValue<unknown>(it.value)
      );
      if (JSON.stringify(kept) !== JSON.stringify(originalCanonical)) {
        resolutions[propertyName] = kept;
      }
    }

    try {
      await submitEventsOptimistic({
        orgId,
        userId: user?.id ?? 0,
        entityTypeName,
        primaryEntityId: sourceId,
        fallbackSeq: sourceObject?.last_event_seq,
        errorToast: false,
        build: b =>
          b.objectMerged(
            sourceId,
            entityTypeName,
            canonicalId,
            Object.keys(resolutions).length > 0 ? resolutions : undefined
          ),
      });

      // `ahoy_object_merged` has no optimistic handler in applyOptimisticBatch
      // (it falls into the "unhandled event type" branch — a harmless
      // console.warn). Visible optimism instead comes from applyObjectMerged
      // below, which redirects the merged-away id to the canonical entity so
      // the list drops the duplicate row immediately, without waiting for the
      // `ahoy_object_merged` WS round-trip. Idempotent with the WS handler
      // (which calls the same store function when the event arrives).
      applyObjectMerged(sourceId, canonicalId);

      const sourceAssociations = associationsByEntityId[sourceId] ?? [];
      const canonicalAssociations = associationsByEntityId[canonicalId] ?? [];

      // Build the planned batch-2 changes as plain data first so we can check
      // whether there's anything to submit — the optimistic-submit helper
      // throws on an empty build.
      const batch2: Array<
        | {
            kind: 'removed';
            associationTypeName: string;
            targetEntityId: string;
          }
        | { kind: 'added'; associationTypeName: string; targetEntityId: string }
      > = [];

      // Dismissed associations → remove from the canonical (post-merge).
      const emitted = new Set<string>();
      for (const assoc of [...canonicalAssociations, ...sourceAssociations]) {
        const key = `${assoc.association_type_name}:${assoc.target_entity_id}`;
        if (dismissedAssociationIds.has(key) && !emitted.has(key)) {
          emitted.add(key);
          batch2.push({
            kind: 'removed',
            associationTypeName: assoc.association_type_name,
            targetEntityId: assoc.target_entity_id,
          });
        }
      }

      // *_to_one association picks. The merge cascade transfers the source's
      // association and cardinality enforcement drops the canonical's — so the
      // SOURCE's target wins by default. When the user kept the CANONICAL's
      // target instead, re-assert it: adding it triggers cardinality
      // enforcement to drop the source's and restore the canonical's. (When the
      // user kept the source's target, the cascade already produced it — no
      // event needed, and re-adding an existing edge is avoided.)
      for (const at of associationTypes ?? []) {
        const isToOne =
          at.cardinality === 'one_to_one' || at.cardinality === 'many_to_one';
        if (!isToOne) {
          continue;
        }
        const canonTarget = canonicalAssociations.find(
          a => a.association_type_name === at.association_type_name
        )?.target_entity_id;
        const srcTarget = sourceAssociations.find(
          a => a.association_type_name === at.association_type_name
        )?.target_entity_id;
        if (!canonTarget || !srcTarget || canonTarget === srcTarget) {
          continue; // no conflict
        }
        const chosen =
          associationChoices[at.association_type_name] ?? canonTarget;
        if (chosen === canonTarget) {
          batch2.push({
            kind: 'added',
            associationTypeName: at.association_type_name,
            targetEntityId: canonTarget,
          });
        }
      }

      if (batch2.length > 0) {
        try {
          await submitEventsOptimistic({
            orgId,
            userId: user?.id ?? 0,
            entityTypeName,
            primaryEntityId: canonicalId,
            fallbackSeq: canonicalObject?.last_event_seq,
            errorToast: false,
            build: b => {
              for (const change of batch2) {
                if (change.kind === 'removed') {
                  b.associationRemoved(
                    canonicalId,
                    entityTypeName,
                    change.associationTypeName,
                    change.targetEntityId
                  );
                } else {
                  b.associationAdded(
                    canonicalId,
                    entityTypeName,
                    change.associationTypeName,
                    change.targetEntityId
                  );
                }
              }
            },
          });
        } catch (err) {
          console.error('Failed to merge associations:', err);
          toast.warning(
            'Merged, but some association changes could not be applied. Try again.'
          );
          // batch 1 already succeeded — merge is valid; close wizard but skip success toast
          onSuccess(canonicalId);
          return;
        }
      }

      toast.success('Records merged successfully.');
      onSuccess(canonicalId);
    } catch (err) {
      if (err instanceof PropertyTypeConflictError) {
        toast.error(
          'This record was updated by someone else. Please try again.'
        );
      } else if (
        err instanceof PropertyTypePermissionError ||
        (err instanceof ApiError && err.statusCode === 403)
      ) {
        toast.error("You don't have permission to merge these records.");
      } else if (err instanceof PropertyTypeValidationError) {
        logger.error('Merge validation failed', err, {
          component: 'crm_merge',
        });
        toast.error(
          "We couldn't merge these records. Check the values and try again."
        );
      } else if (err instanceof ApiError && err.statusCode === 400) {
        logger.error('Merge validation failed', err, {
          component: 'crm_merge',
        });
        toast.error('Merge validation failed.');
      } else {
        logger.error(
          'Merge submit failed',
          err instanceof Error ? err : undefined,
          { component: 'crm_merge' }
        );
        toast.error("We couldn't merge these records. Try again.");
      }
      throw err;
    }
  }, [
    orgId,
    entityTypeName,
    onSuccess,
    associationsByEntityId,
    canonicalId,
    sourceId,
    singlePropertyResolutions,
    multiPropertyResolutions,
    dismissedAssociationIds,
    associationChoices,
    canonicalObject,
    sourceObject,
    schema,
    associationTypes,
    user?.id,
  ]);

  return { submit };
}
